import type { LngLat } from "./types.ts";

export interface MergeResult {
  points: LngLat[];
  segments: Array<[number, number]>;
  start: number | null;
  /** The merged point, which stays selected. */
  index: number;
  /**
   * Old point index → new index. Dropped points are -1 so the draft scene can
   * remove their markers. Segments are rewired separately onto `index`.
   */
  pointMap: Int32Array;
}

/** Mean of the selected coordinates. Empty when none of the indexes exist. */
export function centroid(points: readonly LngLat[], selected: readonly number[]): LngLat | null {
  let lng = 0;
  let lat = 0;
  let count = 0;
  for (const index of selected) {
    const point = points[index];
    if (!point) continue;
    lng += point[0];
    lat += point[1];
    count += 1;
  }
  if (count === 0) return null;
  return [lng / count, lat / count];
}

/**
 * Order for a join chain.
 * A click sequence that still names every selected point is kept.
 * Lasso, box, circle, and select-all leave `clickOrder` null, so the chain
 * is nearest-neighbor from `anchor` (the first point that was selected).
 */
export function chainOrder(
  points: readonly LngLat[],
  selected: readonly number[],
  clickOrder: readonly number[] | null,
  anchor: number | null,
): number[] {
  const seen = new Set<number>();
  const unique: number[] = [];
  for (const index of selected) {
    if (!Number.isInteger(index) || index < 0 || index >= points.length || seen.has(index)) continue;
    seen.add(index);
    unique.push(index);
  }
  if (unique.length <= 2) {
    if (unique.length === 2 && clickOrder) {
      const ordered = uniqueIn(clickOrder, seen);
      if (ordered.length === 2) return ordered;
    }
    return unique;
  }
  if (clickOrder) {
    const ordered = uniqueIn(clickOrder, seen);
    if (ordered.length === unique.length) return ordered;
  }
  const start = anchor != null && seen.has(anchor) ? anchor : unique[0];
  if (start == null) return unique;
  return nearestChain(points, unique, start);
}

/** Append the missing links of `order`. Null when every consecutive pair already exists. */
export function joinSegments(
  segments: ReadonlyArray<readonly [number, number]>,
  order: readonly number[],
): Array<[number, number]> | null {
  if (order.length < 2) return null;
  const seen = new Set<string>();
  for (const [a, b] of segments) {
    if (a === b) continue;
    seen.add(edgeKey(a, b));
  }
  const next = segments.map(([a, b]) => [a, b] as [number, number]);
  let added = 0;
  for (let i = 1; i < order.length; i++) {
    const a = order[i - 1];
    const b = order[i];
    if (a == null || b == null || a === b) continue;
    const key = edgeKey(a, b);
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(a < b ? [a, b] : [b, a]);
    added += 1;
  }
  return added > 0 ? next : null;
}

/**
 * Collapse `selected` into one point at `at`.
 * Lines that touched any of them now touch that point. A segment whose ends
 * both collapse is dropped, and a repeated pair is kept once.
 */
export function mergePoints(
  points: readonly LngLat[],
  segments: ReadonlyArray<readonly [number, number]>,
  start: number | null,
  selected: readonly number[],
  at: LngLat,
): MergeResult | null {
  const chosen = new Set<number>();
  for (const index of selected) {
    if (Number.isInteger(index) && index >= 0 && index < points.length) chosen.add(index);
  }
  if (chosen.size < 2) return null;
  let keep = Infinity;
  for (const index of chosen) if (index < keep) keep = index;

  const pointMap = new Int32Array(points.length).fill(-1);
  const nextPoints: LngLat[] = [];
  let survivor = -1;
  for (let i = 0; i < points.length; i++) {
    if (chosen.has(i) && i !== keep) continue;
    const src = points[i];
    if (!src) continue;
    const next = nextPoints.length;
    nextPoints.push(i === keep ? [at[0], at[1]] : [src[0], src[1]]);
    if (i === keep) survivor = next;
    pointMap[i] = next;
  }
  if (survivor < 0) return null;

  const graphOf = (index: number): number => (chosen.has(index) ? survivor : (pointMap[index] ?? -1));
  const seen = new Set<string>();
  const nextSegments: Array<[number, number]> = [];
  for (const [a, b] of segments) {
    const na = graphOf(a);
    const nb = graphOf(b);
    if (na < 0 || nb < 0 || na === nb) continue;
    const key = edgeKey(na, nb);
    if (seen.has(key)) continue;
    seen.add(key);
    nextSegments.push(na < nb ? [na, nb] : [nb, na]);
  }

  let nextStart: number | null = null;
  if (start != null && start >= 0 && start < points.length) {
    const mapped = graphOf(start);
    nextStart = mapped >= 0 ? mapped : null;
  }
  return {
    points: nextPoints,
    segments: nextSegments,
    start: nextStart,
    index: survivor,
    pointMap,
  };
}

function uniqueIn(order: readonly number[], wanted: ReadonlySet<number>): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const index of order) {
    if (!wanted.has(index) || seen.has(index)) continue;
    seen.add(index);
    out.push(index);
  }
  return out;
}

/** Equirectangular nearest neighbor. Ties keep the lower index. */
function nearestChain(points: readonly LngLat[], selected: readonly number[], start: number): number[] {
  const origin = points[start];
  const cos = Math.cos(((origin?.[1] ?? 0) * Math.PI) / 180);
  const x = new Float64Array(points.length);
  const y = new Float64Array(points.length);
  for (const index of selected) {
    const point = points[index];
    if (!point) continue;
    x[index] = point[0] * cos;
    y[index] = point[1];
  }
  const left = new Set(selected.filter((index) => index !== start));
  const order = [start];
  let current = start;
  while (left.size > 0) {
    let best = -1;
    let bestDist = Infinity;
    const cx = x[current] ?? 0;
    const cy = y[current] ?? 0;
    for (const index of left) {
      const dx = (x[index] ?? 0) - cx;
      const dy = (y[index] ?? 0) - cy;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist || (dist === bestDist && (best < 0 || index < best))) {
        best = index;
        bestDist = dist;
      }
    }
    if (best < 0) break;
    left.delete(best);
    order.push(best);
    current = best;
  }
  return order;
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}
